import { KSA_POLICY, type PublicHolidayRule } from './tables.js';

/**
 * The KSA working calendar: Sunday–Thursday week, public holidays anchored in
 * two calendars (fixed Gregorian dates + Hijri dates resolved through the
 * browser/Node-native `Intl` **Umm al-Qura** calendar — zero dependencies).
 *
 * All computation is UTC for determinism (dates are calendar facts, not
 * instants); callers pass `Date` values built with `Date.UTC`.
 */
export interface HijriParts {
  readonly year: number;
  readonly month: number; // 1–12 (Muharram = 1)
  readonly day: number; // 1–30
}

const HIJRI_FMT = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', {
  timeZone: 'UTC',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

/** Resolve a date's Umm al-Qura Hijri year/month/day. */
export function hijriParts(date: Date): HijriParts {
  const parts = HIJRI_FMT.formatToParts(date);
  const num = (type: 'year' | 'month' | 'day'): number =>
    Number.parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  return { year: num('year'), month: num('month'), day: num('day') };
}

/** Localised Hijri rendering for the UI (e.g. `ar-SA` gets Arabic month names). */
export function formatHijri(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(`${locale}-u-ca-islamic-umalqura`, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/** Friday/Saturday weekend (working week is Sunday–Thursday). */
export function isWeekend(date: Date): boolean {
  return KSA_POLICY.workweek.weekendDays.includes(date.getUTCDay());
}

export interface HolidayMatch {
  readonly name: string;
  readonly ruleRef: string;
}

function inBlock(rule: PublicHolidayRule, month: number, day: number): boolean {
  return month === rule.month && day >= rule.day && day < rule.day + rule.lengthDays;
}

/** The public holiday covering this date, if any (weekends are separate). */
export function publicHoliday(date: Date): HolidayMatch | null {
  const hijri = hijriParts(date);
  const gregorianMonth = date.getUTCMonth() + 1;
  const gregorianDay = date.getUTCDate();

  for (const rule of KSA_POLICY.publicHolidays) {
    const matches =
      rule.calendar === 'gregorian'
        ? inBlock(rule, gregorianMonth, gregorianDay)
        : inBlock(rule, hijri.month, hijri.day);
    if (matches) return { name: rule.name, ruleRef: rule.ruleRef };
  }
  return null;
}

/** A working day is neither weekend nor public holiday. */
export function isWorkingDay(date: Date): boolean {
  return !isWeekend(date) && publicHoliday(date) === null;
}
